import { getHomeboxServiceUrl } from "../utils/urlConfig";
import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import { AuthContext } from '../context/AuthContext';
import {
    fetchProfileDetails,
    updateAuthProfile,
    updatePassword,
    updateBio,
    uploadProfileImage
} from '../services/profileService';
import styles from './Profile.module.css';

const ProfilePage = () => {
    const { user, loginUser, setProfileImage } = useContext(AuthContext);
    const [profile, setProfile] = useState(null);
    const [name, setName] = useState(user?.user_name || '');
    const [email, setEmail] = useState(user?.user_email || '');
    const [bio, setBio] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(true);

    const API_BASE_URL = getHomeboxServiceUrl();
    // Construct full image URL if path exists
    const getProfileImageUrl = (path) => {
        if (!path) return 'https://via.placeholder.com/150?text=Profile';
        if (path.startsWith('http')) return path;
        // The path in DB starts with /public/images/profile/
        // Our API_BASE_URL is usually http://localhost:8082/api
        // But images are served from http://localhost:8082/public/...
        const baseUrl = API_BASE_URL.replace('/api', '');
        return `${baseUrl}${path}`;
    };

    useEffect(() => {
        loadProfile();
    }, []);

    const loadProfile = async () => {
        setLoading(true);
        const result = await fetchProfileDetails();
        if (result.success) {
            setProfile(result.data);
            setBio(result.data.bio || '');
        }
        setLoading(false);
    };

    const handleInfoUpdate = async (e) => {
        e.preventDefault();
        const result = await updateAuthProfile(name, email);
        if (result.success) {
            toast.success('Personal information updated!');
            // Update context if necessary (assuming loginUser updates state)
            const updatedUser = { ...user, user_name: name, user_email: email };
            localStorage.setItem('user', JSON.stringify(updatedUser));
            // Force context update if possible, or just standard refresh
        } else {
            toast.error(result.error || 'Failed to update personal information');
        }
    };

    const handlePasswordUpdate = async (e) => {
        e.preventDefault();
        if (confirmPassword && newPassword !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }
        const result = await updatePassword(newPassword);
        if (result.success) {
            toast.success('Password changed successfully!');
            setNewPassword('');
            setConfirmPassword('');
        } else {
            toast.error(result.error || 'Failed to change password');
        }
    };

    const handleBioUpdate = async () => {
        const result = await updateBio(bio);
        if (result.success) {
            toast.success('Bio updated!');
        } else {
            toast.error(result.error || 'Failed to update bio');
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const result = await uploadProfileImage(file);
        if (result.success) {
            setProfile({ ...profile, profileImagePath: result.data.imagePath });
            setProfileImage(result.data.imagePath); // Update global context
            toast.success('Profile image updated!');
        } else {
            toast.error(result.error || 'Failed to update profile image');
        }
    };

    if (loading) return <div className={styles.loading}>Loading Profile...</div>;

    return (
        <div className={styles.profileContainer}>
            <div className={styles.header}>
                <h1>User Profile</h1>
                <p>Manage your account settings and personal information</p>
            </div>

            <div className={styles.profileGrid}>
                {/* Left Column: Image & Bio */}
                <div className={styles.sidebar}>
                    <div className={styles.card}>
                        <div className={styles.avatarSection}>
                            <img
                                src={getProfileImageUrl(profile?.profileImagePath)}
                                alt="Profile"
                                className={styles.avatar}
                            />
                            <div className={styles.uploadOverlay}>
                                <label htmlFor="avatar-upload">
                                    <i className="fa-solid fa-camera"></i>
                                    {/* <span>Change Photo</span> */}
                                </label>
                                <input
                                    id="avatar-upload"
                                    type="file"
                                    hidden
                                    onChange={handleImageUpload}
                                    accept="image/*"
                                />
                            </div>
                        </div>
                        <div className={styles.userInfo}>
                            <h3>{user?.user_name}</h3>
                            <span className={styles.badge}>{user?.role || 'User'}</span>
                        </div>
                        <div className={styles.bioSection}>
                            <label>About Me</label>
                            <textarea
                                value={bio}
                                onChange={(e) => setBio(e.target.value)}
                                placeholder="Write something about yourself..."
                            />
                            <button className={styles.saveBtn} onClick={handleBioUpdate}>Save Bio</button>
                        </div>
                    </div>
                </div>

                {/* Right Column: Forms */}
                <div className={styles.main}>
                    <div className={styles.card}>
                        <h3>Personal Information</h3>
                        <form onSubmit={handleInfoUpdate} className={styles.form}>
                            <div className={styles.inputGroup}>
                                <label>Full Name</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                />
                            </div>
                            <div className={styles.inputGroup}>
                                <label>Email Address</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>
                            <button type="submit" className={styles.submitBtn}>Update Profile</button>
                        </form>
                    </div>

                    <div className={styles.card}>
                        <h3>Security</h3>
                        <form onSubmit={handlePasswordUpdate} className={styles.form}>
                            <div className={styles.inputGroup}>
                                <label>New Password</label>
                                <input
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Enter at least 8 characters"
                                    required
                                />
                            </div>
                            <div className={styles.inputGroup}>
                                <label>Confirm Password</label>
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                />
                            </div>
                            <button type="submit" className={styles.submitBtn}>Change Password</button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfilePage;
